import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, List, Typography, Space, Button, Grid, Tag } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import type { Customer } from '../../types/customers';
import { DataTable, type AntDColumn } from '../common/DataTable';

const { Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

interface CustomersTableProps {
  customers: Customer[];
  onEdit?: (customer: Customer) => void;
  onView?: (customer: Customer) => void;
  loading?: boolean;
  pagination?: any;
  onTableChange?: (pagination: any) => void;
}

const CustomersTable: React.FC<CustomersTableProps> = ({
  customers,
  onEdit,
  onView,
  loading = false,
  pagination,
  onTableChange,
}) => {
  const { t } = useTranslation(['sales', 'common']);
  const screens = useBreakpoint();

  const columns: AntDColumn<Customer>[] = [
    {
      id: 'property_name',
      label: t('sales:customers.table.property_name'),
      dataIndex: 'property_name',
      key: 'property_name',
      width: 160,
      render: (_: string, record: Customer) => (
        <Button type="link" onClick={() => onView?.(record)}>{record.property_name}</Button>
      ),
      fixed: 'left',
    },
    {
      id: 'status',
      label: t('sales:customers.table.status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => <Tag>{t(`sales:customers.status.${status.toLowerCase().replace(' ', '_')}`)}</Tag>,
    },
    {
      id: 'primary_contact',
      label: t('sales:customers.table.primary_contact'),
      dataIndex: ['primary_contact'],
      key: 'primary_contact',
      width: 160,
      render: (_: any, record: Customer) => record.primary_contact?.name || '-',
    },
    {
      id: 'city',
      label: t('sales:customers.table.city'),
      key: 'city',
      width: 120,
      render: (_: any, record: Customer) => {
        const propertyAddress = record.addresses?.find(a => a.type === 'Property');
        return propertyAddress?.city || '-';
      },
    },
    {
      id: 'actions',
      label: t('common:actions'),
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_: any, record: Customer) => (
        <Space>
          {onEdit && (
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={e => {
                e.stopPropagation();
                onEdit(record);
              }}
            >
              {t('common:edit')}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  if (screens.xs) {
    return (
      <List
        grid={{ gutter: 16, xs: 1, sm: 1 }}
        dataSource={customers}
        loading={loading}
        renderItem={(customer: Customer) => {
          const propertyAddress = customer.addresses?.find(a => a.type === 'Property');
          return (
            <List.Item key={customer.id}>
              <Card
                title={<Text strong>{customer.property_name}</Text>}
                style={{ width: '100%' }}
                actions={[
                  onEdit && (
                    <Button
                      key="edit"
                      type="link"
                      icon={<EditOutlined />}
                      onClick={() => onEdit(customer)}
                    >
                      {t('common:edit')}
                    </Button>
                  ),
                ].filter(Boolean) as React.ReactNode[]}
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Paragraph>
                    <Text strong>{t('sales:customers.table.status')}: </Text>
                    <Tag>{t(`sales:customers.status.${customer.status.toLowerCase().replace(' ', '_')}`)}</Tag>
                  </Paragraph>
                  <Paragraph>
                    <Text strong>{t('sales:customers.table.primary_contact')}: </Text>
                    {customer.primary_contact?.name || '-'}
                  </Paragraph>
                  <Paragraph>
                    <Text strong>{t('sales:customers.table.city')}: </Text>
                    {propertyAddress?.city || '-'}
                  </Paragraph>
                </Space>
              </Card>
            </List.Item>
          );
        }}
      />
    );
  }

  return (
    <DataTable<Customer>
      columns={columns}
      data={customers}
      getRowId={(row: Customer) => row.id}
      loading={loading}
      scroll={{ x: 700 }}
      pagination={pagination}
      onChange={onTableChange}
      showViewAction={false}
    />
  );
};

export default CustomersTable; 